import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Phone, Mail } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow } from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import PropertyConcierge from "@/components/PropertyConcierge";
import Testimonials from "@/components/Testimonials";
import FaqSection from "@/components/FaqSection";
import ConsultationSection from "@/components/ConsultationSection";
import { CONTACT, IMAGES, PRACTICE_AREAS } from "@/data/site";

const CHAPTERS = [
  {
    number: "01",
    title: "Clarity before counsel",
    text: "Legal matters are explained in plain language, so you always understand your options before you decide.",
  },
  {
    number: "02",
    title: "Personal attention",
    text: "Every matter receives careful, individual focus — you are never just a file number at Annwil Law Ja.",
  },
  {
    number: "03",
    title: "Jamaica & the diaspora",
    text: "Most of our clients live overseas — we make Jamaican legal matters simple to manage from anywhere in the world.",
  },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} className="relative min-h-[92vh] flex items-end overflow-hidden bg-navy" data-testid="home-hero">
      <motion.div className="absolute inset-0 will-change-transform" style={{ y }}>
        <img
          src={IMAGES.heroOffice}
          alt="Elegant law office interior"
          className="w-full h-[120%] object-cover opacity-25 saturate-[0.5]"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/75 to-navy/50" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pb-20 pt-40 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="text-xs md:text-sm uppercase tracking-[0.3em] text-gold mb-8"
        >
          Attorney-At-Law · Jamaica & the Diaspora
        </motion.p>
        <h1 className="font-serif font-light text-cream text-5xl md:text-7xl lg:text-8xl leading-[1.02] tracking-tight max-w-5xl">
          <MaskedLine delay={0.15}>Your trusted legal</MaskedLine>
          <MaskedLine delay={0.3}>partner for <em className="italic text-gold">life's</em></MaskedLine>
          <MaskedLine delay={0.45}><em className="italic text-gold">important matters.</em></MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="text-cream/75 text-base md:text-lg max-w-xl mt-8 leading-relaxed"
        >
          Annwil Law Ja provides approachable and professional legal services in
          family law, real estate law, probate and estate planning — clear
          guidance to help you move forward with confidence.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-wrap gap-4 mt-10"
        >
          <Link
            to="/contact"
            data-testid="hero-book-consultation-button"
            className="group inline-flex items-center gap-3 rounded-full bg-gold text-navy text-sm font-bold px-8 py-4 transition-colors duration-300 hover:bg-cream"
          >
            Book a Consultation
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/services"
            data-testid="hero-explore-services-button"
            className="inline-flex items-center gap-3 rounded-full border border-cream/40 text-cream text-sm font-semibold px-8 py-4 transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            Explore Our Services
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const PracticeOverview = () => (
  <section className="py-24 md:py-32" data-testid="home-practice-areas">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <Reveal>
        <Eyebrow>Practice Areas</Eyebrow>
        <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight text-navy max-w-3xl text-balance">
          Legal support for life's important moments.
        </h2>
        <p className="text-muted-foreground max-w-xl mt-6 text-base md:text-lg">
          Whether you are dealing with a family matter, a property transaction
          or planning for the future, Annwil Law Ja is here to provide
          professional legal guidance.
        </p>
      </Reveal>

      <div className="grid md:grid-cols-2 gap-6 mt-16">
        {PRACTICE_AREAS.map((area, i) => (
          <Reveal key={area.id} delay={i * 0.1} className={i === 0 ? "md:row-span-2" : i === 3 ? "md:col-span-2" : ""}>
            <Link
              to={`/services#${area.id}`}
              data-testid={`practice-card-${area.id}`}
              className={`group relative block overflow-hidden border border-navy/10 bg-sand ${
                i === 0 ? "h-full min-h-[420px] md:min-h-full" : i === 3 ? "min-h-[300px]" : "min-h-[280px]"
              }`}
            >
              <img
                src={area.image}
                alt={area.title}
                className="absolute inset-0 w-full h-full object-cover saturate-[0.55] transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-8 md:p-10">
                <span className="font-serif italic text-gold text-lg">{area.number}.</span>
                <h3 className="font-serif text-cream text-3xl md:text-4xl font-light mt-2">{area.title}</h3>
                <p className="text-cream/75 text-sm mt-3 max-w-md">{area.short}</p>
                <span className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em] font-bold mt-5">
                  Learn more
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Chapters = () => (
  <section className="py-24 md:py-32 bg-sand border-y border-navy/10" data-testid="home-trust-chapters">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <Reveal>
        <Eyebrow>Why Annwil Law Ja</Eyebrow>
        <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight text-navy max-w-3xl">
          A practice built on trust.
        </h2>
      </Reveal>
      <div className="mt-16">
        {CHAPTERS.map((chapter, i) => (
          <Reveal key={chapter.number} delay={i * 0.08}>
            <div className="grid md:grid-cols-[140px_1fr_1.4fr] gap-4 md:gap-10 items-baseline border-t border-navy/15 py-10">
              <span className="font-serif italic text-gold-hover text-3xl">{chapter.number}.</span>
              <h3 className="font-serif text-2xl md:text-3xl text-navy font-light">{chapter.title}</h3>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed">{chapter.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const AboutTeaser = () => (
  <section className="py-24 md:py-32" data-testid="home-about-teaser">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <div className="relative overflow-hidden border border-navy/10 group max-w-md">
          <img
            src={IMAGES.principal}
            alt="Anique Wilson Grey, Attorney-at-Law and Founder of Annwil Law Ja"
            className="w-full h-[420px] md:h-[500px] object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-navy/90 px-6 py-4">
            <p className="font-serif text-cream text-xl">Anique Wilson Grey</p>
            <p className="text-gold text-xs uppercase tracking-[0.2em] font-bold mt-1">Attorney-at-Law · Founder</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <Eyebrow>About Annwil Law Ja</Eyebrow>
        <h2 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-navy">
          Trusted guidance. Personal attention.
        </h2>
        <p className="text-muted-foreground mt-6 text-base md:text-lg leading-relaxed">
          Annwil Law Ja advises clients across Jamaica and throughout the
          diaspora in family law, real estate law, probate and estate
          planning — professional and dependable, while remaining approachable
          and focused on your individual circumstances.
        </p>
        <p className="font-serif italic text-navy text-xl md:text-2xl border-l-2 border-gold pl-6 mt-8">
          “Professional legal service with a personal and approachable touch.”
        </p>
        <Link
          to="/about"
          data-testid="home-about-link"
          className="group inline-flex items-center gap-3 mt-8 text-navy text-sm font-bold uppercase tracking-[0.2em] border-b border-gold pb-1 transition-colors duration-300 hover:text-gold-hover"
        >
          Get to know us
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const LocationSection = () => (
  <section className="py-24 md:py-32 bg-sand border-t border-navy/10" data-testid="home-location">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <Eyebrow>Visit Our Office</Eyebrow>
        <h2 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-navy">
          Based in St. Ann's Bay. <em className="italic text-gold-hover">Serving the world.</em>
        </h2>
        <p className="text-muted-foreground mt-6 text-base md:text-lg leading-relaxed">
          Our office sits in the heart of St. Ann's Bay — but our clients span
          the globe, with most living overseas. Consultations are easily
          arranged by telephone or video call, wherever you are.
        </p>
        <div className="mt-8 space-y-5 text-base md:text-lg">
          <p className="flex items-start gap-3 text-navy">
            <MapPin size={20} className="text-gold-hover mt-1 shrink-0" />
            <span>{CONTACT.addressLines[0]}<br />{CONTACT.addressLines[1]}</span>
          </p>
          <p className="flex items-center gap-3">
            <Phone size={20} className="text-gold-hover shrink-0" />
            <a href={CONTACT.phoneHref} data-testid="location-phone-link" className="text-navy hover:text-gold-hover transition-colors duration-300">{CONTACT.phone}</a>
          </p>
          <p className="flex items-center gap-3">
            <Mail size={20} className="text-gold-hover shrink-0" />
            <a href={CONTACT.emailHref} data-testid="location-email-link" className="text-navy hover:text-gold-hover transition-colors duration-300 break-all">{CONTACT.email}</a>
          </p>
        </div>
        <a
          href={CONTACT.mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="location-directions-button"
          className="group inline-flex items-center gap-3 mt-10 rounded-full bg-navy text-cream text-sm font-bold px-8 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy"
        >
          Get Directions
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="relative overflow-hidden border border-navy/10">
          <img src={IMAGES.heroOffice} alt="Annwil Law Ja office" className="w-full h-[380px] md:h-[460px] object-cover saturate-[0.55]" />
          <div className="absolute bottom-0 left-0 bg-cream px-6 py-4 border-t border-r border-navy/10">
            <p className="font-serif italic text-xl text-navy">Shop 1, 7 Gully Road</p>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">St. Ann's Bay, Jamaica</p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export const CtaBand = () => (
  <section className="relative bg-navy py-24 md:py-32 overflow-hidden" data-testid="cta-band">
    <img
      src={IMAGES.palm}
      alt=""
      aria-hidden="true"
      className="absolute -left-16 bottom-0 w-96 h-96 object-cover opacity-10 saturate-0"
    />
    <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 text-center">
      <Reveal>
        <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-gold mb-6">Ready when you are</p>
        <h2 className="font-serif font-light text-cream text-4xl md:text-6xl lg:text-7xl tracking-tight text-balance">
          Book a consultation <em className="italic text-gold">today.</em>
        </h2>
        <p className="text-cream/70 max-w-xl mx-auto mt-6 text-base md:text-lg">
          Tell us briefly about your legal matter and we will guide you through
          the next steps.
        </p>
        <Link
          to="/contact"
          data-testid="cta-band-book-button"
          className="group inline-flex items-center gap-3 rounded-full bg-gold text-navy text-sm font-bold px-10 py-5 mt-10 transition-colors duration-300 hover:bg-cream"
        >
          Book a Consultation
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <EditorialMarquee dark={false} />
    <PracticeOverview />
    <Chapters />
    <AboutTeaser />
    <PropertyConcierge />
    <Testimonials />
    <LocationSection />
    <FaqSection />
    <ConsultationSection />
  </>
);

export default Home;
